import React, { useEffect } from 'react';
import { Search } from 'lucide-react';
import { DEFAULT_DEBOUNCE_MS } from '../hooks/useDebouncedValue.ts';

export interface ListPageSearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  /** Extra classes on the `<input>` (wrapper is unchanged). */
  inputClassName?: string;
  /**
   * When true, calls `onDebouncedChange` after `debounceMs` when `value` changes.
   * When false (default), behaves as a normal controlled/uncontrolled input.
   */
  isDebounce?: boolean;
  /** Debounce delay in ms. Default {@link DEFAULT_DEBOUNCE_MS}. */
  debounceMs?: number;
  /** Invoked after debounce when `isDebounce` is true. */
  onDebouncedChange?: (value: string) => void;
  /** Trim string before `onDebouncedChange`. Default true. */
  trimDebounced?: boolean;
}

/**
 * Search field with leading icon. Styling matches SOX Risks (`Risks.js`).
 * Pass `isDebounce` + `onDebouncedChange` to drive API queries without refetching on every keystroke.
 */
export function ListPageSearchInput({
  inputClassName,
  id,
  isDebounce = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onDebouncedChange,
  trimDebounced = true,
  value,
  ...props
}: ListPageSearchInputProps) {
  useEffect(() => {
    if (!isDebounce || !onDebouncedChange) return undefined;

    const emit = () => {
      const raw = typeof value === 'string' ? value : String(value ?? '');
      onDebouncedChange(trimDebounced ? raw.trim() : raw);
    };

    const timer = window.setTimeout(emit, debounceMs);
    return () => window.clearTimeout(timer);
  }, [isDebounce, onDebouncedChange, value, debounceMs, trimDebounced]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden />
      <input
        id={id}
        type="text"
        value={value}
        className={[
          'w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
          inputClassName ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      />
    </div>
  );
}
